/**
 * Touch → mouse gesture adapter for the Unreal Pixel Streaming iframe.
 *
 * The iframe is same-origin (player.html apex-pinned), so we can attach
 * listeners inside its document and synthesize the pointer/mouse events
 * Epic's player forwards to Unreal over the data channel.
 *
 * Gestures (per the mobile plan):
 * - single tap   -> left click at point (presses the in-video menu)
 * - double tap   -> left click + a dedicated 'double_tap' UIInteraction
 *                   (the plugin's mobile mode binds move-to-point to it; see
 *                   Workstream B contract B4)
 * - 1-finger drag-> mousedown at origin + mousemove stream + mouseup
 *                   (matches the hold-to-look mouse scheme = rotate)
 *
 * Degrades silently: any cross-origin/access error means Epic's native touch
 * handling stays in charge.
 */

const DOUBLE_TAP_MS = 300;
const DOUBLE_TAP_SLOP_PX = 30;
const DRAG_SLOP_PX = 8;

function findVideo(doc) {
  return doc.querySelector('video') || doc.querySelector('#streamingVideo');
}

function dispatchPointerAndMouse(target, doc, type, x, y, buttons) {
  // Epic's frontend binds pointer events on newer builds and mouse events on
  // older ones — dispatch both; duplicates are harmless (the player only
  // listens to one family).
  const base = {
    bubbles: true,
    cancelable: true,
    view: doc.defaultView,
    clientX: x,
    clientY: y,
    button: 0,
    buttons,
  };
  try {
    const pointerType = { down: 'pointerdown', move: 'pointermove', up: 'pointerup' }[type];
    target.dispatchEvent(new doc.defaultView.PointerEvent(pointerType, {
      ...base,
      pointerId: 1,
      pointerType: 'mouse', // present as a mouse so the player uses its mouse path
      isPrimary: true,
    }));
  } catch (e) { /* PointerEvent unavailable — mouse events below still fire */ }
  const mouseType = { down: 'mousedown', move: 'mousemove', up: 'mouseup' }[type];
  target.dispatchEvent(new doc.defaultView.MouseEvent(mouseType, base));
}

function synthesizeClick(target, doc, x, y) {
  dispatchPointerAndMouse(target, doc, 'down', x, y, 1);
  dispatchPointerAndMouse(target, doc, 'up', x, y, 0);
}

/**
 * Attach the adapter. Returns a detach() function, or null if the document /
 * video element is not reachable (caller should just do nothing then).
 *
 * opts.onDoubleTap(normX, normY) — normalized [0..1] coords, used by the
 * caller to emit the 'double_tap' UIInteraction toward the plugin UI.
 */
export function attachTouchAdapter(iframeDoc, opts = {}) {
  let video;
  try {
    video = findVideo(iframeDoc);
  } catch (e) {
    return null;
  }
  if (!video) return null;

  // Kill browser gestures (scroll/zoom/double-tap-zoom) on the video.
  const styleEl = iframeDoc.createElement('style');
  styleEl.setAttribute('data-touch-adapter', '1');
  styleEl.textContent = 'video { touch-action: none !important; } body { overscroll-behavior: none; }';
  iframeDoc.head.appendChild(styleEl);

  let lastTapTime = 0;
  let lastTapX = 0;
  let lastTapY = 0;
  let touchState = null; // { startX, startY, dragging }

  const onTouchStart = (e) => {
    if (e.touches.length !== 1) { touchState = null; return; }
    e.preventDefault();
    const t = e.touches[0];
    touchState = { startX: t.clientX, startY: t.clientY, dragging: false };
  };

  const onTouchMove = (e) => {
    if (!touchState || e.touches.length !== 1) return;
    e.preventDefault();
    const t = e.touches[0];
    const dx = t.clientX - touchState.startX;
    const dy = t.clientY - touchState.startY;
    if (!touchState.dragging) {
      if (Math.hypot(dx, dy) < DRAG_SLOP_PX) return;
      touchState.dragging = true;
      // Start the hold-to-look: press at the origin point.
      dispatchPointerAndMouse(video, iframeDoc, 'down', touchState.startX, touchState.startY, 1);
    }
    dispatchPointerAndMouse(video, iframeDoc, 'move', t.clientX, t.clientY, 1);
  };

  const onTouchEnd = (e) => {
    if (!touchState) return;
    e.preventDefault();
    const t = e.changedTouches[0];
    if (touchState.dragging) {
      // End of rotate drag.
      dispatchPointerAndMouse(video, iframeDoc, 'up', t.clientX, t.clientY, 0);
    } else {
      // A tap. Single = click; double = click + double_tap signal.
      const now = Date.now();
      const isDouble =
        now - lastTapTime < DOUBLE_TAP_MS &&
        Math.hypot(t.clientX - lastTapX, t.clientY - lastTapY) < DOUBLE_TAP_SLOP_PX;
      synthesizeClick(video, iframeDoc, t.clientX, t.clientY);
      if (isDouble && typeof opts.onDoubleTap === 'function') {
        const rect = video.getBoundingClientRect();
        const nx = rect.width > 0 ? (t.clientX - rect.left) / rect.width : 0;
        const ny = rect.height > 0 ? (t.clientY - rect.top) / rect.height : 0;
        opts.onDoubleTap(Math.min(Math.max(nx, 0), 1), Math.min(Math.max(ny, 0), 1));
        lastTapTime = 0; // reset so triple-tap doesn't double-fire
      } else {
        lastTapTime = now;
        lastTapX = t.clientX;
        lastTapY = t.clientY;
      }
    }
    touchState = null;
  };

  const onTouchCancel = () => {
    if (touchState && touchState.dragging) {
      dispatchPointerAndMouse(video, iframeDoc, 'up', touchState.startX, touchState.startY, 0);
    }
    touchState = null;
  };

  const listenerOpts = { passive: false };
  video.addEventListener('touchstart', onTouchStart, listenerOpts);
  video.addEventListener('touchmove', onTouchMove, listenerOpts);
  video.addEventListener('touchend', onTouchEnd, listenerOpts);
  video.addEventListener('touchcancel', onTouchCancel, listenerOpts);

  return function detach() {
    try {
      video.removeEventListener('touchstart', onTouchStart, listenerOpts);
      video.removeEventListener('touchmove', onTouchMove, listenerOpts);
      video.removeEventListener('touchend', onTouchEnd, listenerOpts);
      video.removeEventListener('touchcancel', onTouchCancel, listenerOpts);
      styleEl.remove();
    } catch (e) { /* iframe may already be gone */ }
  };
}

/**
 * Poll the iframe document for the <video> element (the player creates it
 * after WebRTC negotiation) and attach when it appears.
 * Returns a cancel() that also detaches if already attached.
 */
export function attachTouchAdapterWhenReady(getIframeDoc, opts = {}, { intervalMs = 500, timeoutMs = 60000 } = {}) {
  let cancelled = false;
  let detach = null;
  const startedAt = Date.now();

  const tick = () => {
    if (cancelled) return;
    let doc = null;
    try {
      doc = getIframeDoc();
    } catch (e) { /* cross-origin — give up silently */ return; }
    if (doc) {
      const d = attachTouchAdapter(doc, opts);
      if (d) {
        detach = d;
        return; // attached — stop polling
      }
    }
    if (Date.now() - startedAt < timeoutMs) {
      setTimeout(tick, intervalMs);
    }
  };
  tick();

  return function cancel() {
    cancelled = true;
    if (detach) detach();
  };
}
